import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Settings as SettingsIcon,
  Clock,
  Bell,
  Calendar,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Save,
  Factory,
  Smartphone,
  XOctagon,
  ClipboardX,
} from "lucide-react";
import PWASettings from "../components/PWASettings";
import ReasonManager from "../components/ReasonManager";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [factorySettings, setFactorySettings] = useState({
    factory_name: "EP3D ENGINEERING",
    timezone: "Europe/Lisbon",
    work_start_hour: 8,
    work_end_hour: 17,
    delay_check_hour: 6,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("mes_token");
      
      // Fetch scheduler status
      try {
        const schedulerRes = await axios.get(`${API}/planning/scheduler-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSchedulerStatus(schedulerRes.data);
      } catch (e) {
        console.error("Error fetching scheduler status:", e);
      }

      // Fetch factory settings
      try {
        const settingsRes = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (settingsRes.data) {
          setFactorySettings(prev => ({ ...prev, ...settingsRes.data }));
        }
      } catch (e) {
        // Settings might not exist yet
      }

    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("mes_token");
      
      await axios.post(`${API}/settings`, factorySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Configurações guardadas com sucesso!");
      await fetchSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erro ao guardar configurações");
    } finally {
      setSaving(false);
    }
  };

  const triggerDelayCheck = async () => {
    try {
      const token = localStorage.getItem("mes_token");
      const res = await axios.post(`${API}/planning/check-delays`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.delayed_orders > 0) {
        toast.warning(`${res.data.delayed_orders} ordem(s) atrasada(s) encontrada(s)!`);
      } else {
        toast.success("Não há ordens atrasadas!");
      }
    } catch (error) {
      toast.error("Erro ao verificar atrasos");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-foreground flex items-center gap-3">
            <SettingsIcon className="h-8 w-8 text-primary" />
            Configurações do Sistema
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerir configurações da fábrica, aplicação e agendamentos
          </p>
        </div>
      </div>

      <Tabs defaultValue="factory" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="factory" className="flex items-center gap-2">
            <Factory className="h-4 w-4" />
            Fábrica
          </TabsTrigger>
          <TabsTrigger value="stopReasons" className="flex items-center gap-2" data-testid="tab-stop-reasons">
            <XOctagon className="h-4 w-4" />
            Motivos Paragem
          </TabsTrigger>
          <TabsTrigger value="defectReasons" className="flex items-center gap-2" data-testid="tab-defect-reasons">
            <ClipboardX className="h-4 w-4" />
            Motivos Rejeição
          </TabsTrigger>
          <TabsTrigger value="app" className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            Aplicação
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="factory">
          <div className="flex justify-end mb-4">
            <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar Configurações
            </Button>
          </div>
          
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Factory Settings */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5 text-blue-500" />
              Configurações da Fábrica
            </CardTitle>
            <CardDescription>
              Informações gerais e horário de funcionamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Fábrica</Label>
              <Input
                value={factorySettings.factory_name}
                onChange={(e) => setFactorySettings({ ...factorySettings, factory_name: e.target.value })}
                placeholder="Nome da empresa"
                data-testid="factory-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Fuso Horário</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={factorySettings.timezone}
                onChange={(e) => setFactorySettings({ ...factorySettings, timezone: e.target.value })}
                data-testid="timezone-select"
              >
                <option value="Europe/Lisbon">Portugal (UTC+0/+1)</option>
                <option value="Europe/Madrid">Espanha (UTC+1/+2)</option>
                <option value="Europe/London">Reino Unido (UTC+0/+1)</option>
                <option value="Europe/Paris">França (UTC+1/+2)</option>
                <option value="America/Sao_Paulo">Brasil (UTC-3)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora de Início</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                  value={factorySettings.work_start_hour}
                  onChange={(e) => setFactorySettings({ ...factorySettings, work_start_hour: parseInt(e.target.value) })}
                  data-testid="work-start-select"
                >
                  {[...Array(24)].map((_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Hora de Fim</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                  value={factorySettings.work_end_hour}
                  onChange={(e) => setFactorySettings({ ...factorySettings, work_end_hour: parseInt(e.target.value) })}
                  data-testid="work-end-select"
                >
                  {[...Array(24)].map((_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scheduler Settings */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Scheduler de Verificação de Atrasos
            </CardTitle>
            <CardDescription>
              Verificação automática diária de ordens atrasadas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {schedulerStatus && (
              <div className="p-4 rounded-lg bg-secondary/50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Estado do Scheduler</span>
                  <Badge className={schedulerStatus.scheduler_active ? "bg-green-500" : "bg-red-500"}>
                    {schedulerStatus.scheduler_active ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Ativo
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Inativo
                      </>
                    )}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Próxima execução</span>
                  <span className="text-sm font-mono">
                    {new Date(schedulerStatus.next_run).toLocaleString('pt-PT')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tempo até próxima</span>
                  <span className="text-sm font-mono">
                    {schedulerStatus.hours_until_next_run.toFixed(1)} horas
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Hora da Verificação Diária (UTC)</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={factorySettings.delay_check_hour}
                onChange={(e) => setFactorySettings({ ...factorySettings, delay_check_hour: parseInt(e.target.value) })}
                data-testid="delay-check-hour-select"
              >
                {[...Array(24)].map((_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 UTC</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Nota: A alteração da hora só terá efeito após reinício do servidor
              </p>
            </div>

            <Button 
              variant="outline" 
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={triggerDelayCheck}
              data-testid="trigger-delay-check-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Executar Verificação Agora
            </Button>
          </CardContent>
        </Card>

        {/* Planning Defaults */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-500" />
              Predefinições de Planeamento
            </CardTitle>
            <CardDescription>
              Valores padrão para novas ordens de produção
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Horas de Trabalho por Dia (Padrão)</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={factorySettings.default_hours_per_day || 8}
                onChange={(e) => setFactorySettings({ ...factorySettings, default_hours_per_day: parseInt(e.target.value) })}
                data-testid="default-hours-select"
              >
                <option value={8}>8 horas (1 turno)</option>
                <option value={16}>16 horas (2 turnos)</option>
                <option value={24}>24 horas (3 turnos)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div className="space-y-2">
                <Label>Pesagem obrigatória a cada (min)</Label>
                <Input type="number" min="5" max="480" value={factorySettings.weight_check_interval_min ?? 60}
                  onChange={(e) => setFactorySettings({ ...factorySettings, weight_check_interval_min: parseInt(e.target.value) || 60 })}
                  data-testid="weight-check-interval-input" />
                <p className="text-xs text-muted-foreground">O operador é lembrado (push + painel) quando passa este tempo sem pesar.</p>
              </div>
              <div className="space-y-2">
                <Label>Bloquear registo de produção sem pesagem</Label>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                  value={factorySettings.weight_check_block === false ? "no" : "yes"}
                  onChange={(e) => setFactorySettings({ ...factorySettings, weight_check_block: e.target.value === "yes" })}
                  data-testid="weight-check-block-select">
                  <option value="yes">Sim — bloqueia até pesar</option>
                  <option value="no">Não — apenas lembra</option>
                </select>
                <p className="text-xs text-muted-foreground">Chefes de turno/admin podem forçar o registo.</p>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">ℹ️ Como funciona</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>8h:</strong> 1 turno diurno</li>
                <li>• <strong>16h:</strong> 2 turnos (manhã + tarde)</li>
                <li>• <strong>24h:</strong> 3 turnos (produção contínua)</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                As horas de trabalho afetam o cálculo dos dias estimados para cada ordem.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Info */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5 text-gray-500" />
              Informações do Sistema
            </CardTitle>
            <CardDescription>
              Versão e estado do sistema MES
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-secondary/50">
                <p className="text-xs text-muted-foreground">Versão</p>
                <p className="font-mono font-bold">2.0.0</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/50">
                <p className="text-xs text-muted-foreground">Última Atualização</p>
                <p className="font-mono font-bold">10/03/2026</p>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Sistema Operacional</span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                Todos os serviços estão a funcionar normalmente.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>
        
        <TabsContent value="stopReasons">
          <ReasonManager kind="stop" />
        </TabsContent>
        
        <TabsContent value="defectReasons">
          <ReasonManager kind="defect" />
        </TabsContent>
        
        <TabsContent value="app">
          <PWASettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
